import { useState } from 'react';
import { Box, Tab, Table, TableBody, TableHead, TableRow, Tabs } from '@mui/material';
import {
  CustomButton,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import { useSupplierLedgerProvider } from '../../../hooks/useSupplierLedger';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import { AmountPanel } from './SupplierLedgerShared';

const ProviderDetailDrawer = ({ providerId, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { data: detail, isLoading } = useSupplierLedgerProvider(providerId, { enabled: Boolean(providerId) });
  const provider = detail?.provider || {};
  const tables = [
    {
      label: 'Facturas',
      rows: detail?.invoices || [],
      columns: [
        ['invoice_date', 'Fecha'],
        ['receipt_number', 'Comprobante'],
        ['concept', 'Concepto'],
        ['total_amount_ars', 'Total ARS'],
        ['pending_amount_ars', 'Pendiente ARS'],
        ['status', 'Estado'],
      ],
    },
    {
      label: 'Ajustes',
      rows: detail?.adjustments || [],
      columns: [
        ['adjustment_date', 'Fecha'],
        ['adjustment_type', 'Tipo'],
        ['concept', 'Concepto'],
        ['amount_ars', 'Importe ARS'],
        ['status', 'Estado'],
      ],
    },
    {
      label: 'Órdenes',
      rows: detail?.payment_orders || [],
      columns: [
        ['emission_date', 'Fecha'],
        ['order_number', 'OP'],
        ['applied_to_description', 'Aplicado a'],
        ['total_amount_ars', 'Total ARS'],
        ['status', 'Estado'],
      ],
    },
    {
      label: 'Movimientos',
      rows: detail?.bank_movements || [],
      columns: [
        ['emission_date', 'Fecha'],
        ['concept', 'Concepto'],
        ['amount_ars', 'Importe ARS'],
        ['payment_method', 'Medio'],
        ['is_reconciled', 'Conciliado'],
      ],
    },
  ];
  const activeTable = tables[activeTab];

  return (
    <DrawerForm
      open={Boolean(providerId)}
      onClose={onClose}
      title="Detalle de cuenta corriente"
      width={{ xs: '100vw', md: 'min(1100px, calc(100vw - 40px))' }}
      footer={(
        <DrawerFormActions
          summary={<DrawerFormActionSummary label="Saldo ARS" value={formatAmount(provider.ledger_balance_ars)} />}
        >
          <CustomButton variant="outlined" onClick={onClose}>Cerrar</CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <DrawerFormSection title={provider.provider_name || 'Proveedor'} grid={false}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25 }}>
            <AmountPanel label="Facturado ARS" value={formatAmount(provider.total_invoiced_ars)} />
            <AmountPanel label="Pagado ARS" value={formatAmount(provider.paid_amount_ars)} />
            <AmountPanel label="Ajustes ARS" value={formatAmount(provider.adjustments_balance_ars)} />
            <AmountPanel label="Saldo ARS" value={formatAmount(provider.ledger_balance_ars)} />
          </Box>
        </DrawerFormSection>
        <DrawerFormSection title="Movimientos de cuenta" grid={false}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40, mb: 1 }}>
            {tables.map((table) => <Tab key={table.label} label={table.label} sx={{ minHeight: 40 }} />)}
          </Tabs>
          <GeneralTableBox hasMaxHeight={false} sx={{ minHeight: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {activeTable.columns.map(([, label]) => <HeadTableCell key={label}>{label}</HeadTableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableBodySkeleton rows={5} />
                ) : activeTable.rows.length ? activeTable.rows.map((row, index) => (
                  <TableRow key={row.uuid || index} hover>
                    {activeTable.columns.map(([field]) => {
                      const value = row[field];
                      const isAmount = field.includes('amount') || field.includes('total') || field.includes('pending');
                      return (
                        <RowTableCell key={field}>
                          {isAmount ? formatAmount(value) : (typeof value === 'boolean' ? (value ? 'Sí' : 'No') : value || '-')}
                        </RowTableCell>
                      );
                    })}
                  </TableRow>
                )) : (
                  <NoDataSkeleton colSpan={activeTable.columns.length} message="Sin movimientos para esta vista." showSubtitle={false} />
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        </DrawerFormSection>
      </Box>
    </DrawerForm>
  );
};

export default ProviderDetailDrawer;
